import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
            <i className="ri-error-warning-line text-4xl"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-block px-5 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <div className="min-h-screen pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.seoDescription,
            "image": article.image,
            "author": {
              "@type": "Person",
              "name": article.author,
            },
            "datePublished": article.date,
            "dateModified": article.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://example.com/article/${article.slug}`,
            },
            "publisher": {
              "@type": "Organization",
              "name": "黄油游戏官方指南",
            },
            "keywords": article.seoKeywords,
          }),
        }}
      />

      <Breadcrumb items={breadcrumbItems} />

      <article className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-3">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              {article.featured && (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                  精选
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 leading-tight mb-4 text-balance">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </div>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-xs"></i>
                </div>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </div>

          <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-8 md:mb-10">
            <img
              src={article.image}
              alt={article.imageAlt}
              title={article.title}
              className="w-full h-full object-cover"
              width="1200"
              height="675"
            />
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4
              prose-li:text-foreground-700 prose-li:leading-relaxed prose-li:mb-1.5
              prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 md:mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-400 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6 md:mb-8">
              相关黄油游戏文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop="">
              {relatedArticles.map((related) => (
                <ArticleCard key={related.id} article={related} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}